import * as React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import BasicTabs from '../../components/Tabs';
import { useDrafts } from '../../hooks/useDrafts';
import {
  DraftInterfaceState,
  PostReplyDraftInterfaceState,
} from '../../interfaces/post.interface';
import type { UserState } from '../../interfaces/user.interface';
import PostReplyDraftsTab from '../../pages/Drafts/TabsContent/PostReplyDraftsTab';
import UserDraftsTab from '../../pages/Drafts/TabsContent/UserDraftsTab';
import { draftSlice } from '../../store/draft.store';
import { postReplyDraftSlice } from '../../store/postReplyDraft.store';
import { postReplyDraftsSlice } from '../../store/postReplyDrafts.store';

function Drafts(): React.ReactElement {
  const dispatch = useDispatch();
  const params = useParams();
  const { username } = params;
  const { user } = useSelector((state: UserState) => state.userStore);
  const { isEditingDraft } = useSelector(
    (state: DraftInterfaceState) => state.draftStore,
  );
  const { isEditingPostReplyDraft } = useSelector(
    (state: PostReplyDraftInterfaceState) => state.postReplyDraftStore,
  );
  const {
    RESET_DRAFT_CATEGORIES,
    IS_ADDING_DRAFT_STORE_ACTION,
    RESET_DRAFT_STATE,
    RESET_UPDATED_DRAFT,
  } = draftSlice.actions;
  const { SET_POST_REPLY_DRAFTS_STORE_ACTION } = postReplyDraftsSlice.actions;
  const { RESET_POST_REPLY_DRAFT_STATE, RESET_POST_REPLY_UPDATED_DRAFT } =
    postReplyDraftSlice.actions;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { id } = user;

  const { postDrafts, replyDrafts, loading, error, refetch } = useDrafts(
    user.id,
  );

  // Keep Redux populated for not-yet-migrated readers (removed in a later task)
  // and run the editor-UI resets when the list (re)loads.
  // biome-ignore lint/correctness/useExhaustiveDependencies: stable slice action refs intentionally omitted
  React.useEffect(() => {
    if (!loading) {
      dispatch(RESET_UPDATED_DRAFT());
      dispatch(RESET_POST_REPLY_UPDATED_DRAFT());
      dispatch(RESET_DRAFT_CATEGORIES());
      dispatch(SET_POST_REPLY_DRAFTS_STORE_ACTION(replyDrafts));
      dispatch(RESET_DRAFT_STATE(true));
      dispatch(RESET_POST_REPLY_DRAFT_STATE(true));
    }
  }, [postDrafts, replyDrafts, loading]);

  const handleAddDraft = () => {
    setIsModalOpen(true);
    dispatch(IS_ADDING_DRAFT_STORE_ACTION(true));
  };

  const tabsConfig = [
    {
      id: 'userDraftsTab',
      label: 'User Drafts',
      content: (
        <UserDraftsTab
          isLoading={loading}
          username={username}
          isModalOpen={isModalOpen}
          handleAddDraft={handleAddDraft}
          refetch={refetch}
          setIsModalOpen={setIsModalOpen}
          id={id}
          isEditingDraft={isEditingDraft as boolean}
          userDraftPosts={postDrafts}
          error={error}
        />
      ),
    },
    {
      id: 'postRepliesInDraftModeTab',
      label: 'Post Reply Drafts',
      content: (
        <PostReplyDraftsTab
          username={username}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          id={id}
          isEditingDraft={isEditingPostReplyDraft as boolean}
          postReplyDrafts={replyDrafts}
        />
      ),
    },
  ];

  return <BasicTabs tabs={tabsConfig} defaultValue={0} />;
}

export default Drafts;
